import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { AccountRoleService } from 'auth/account-role.service';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';
import { environment } from 'environments/environment';

import {
  GetCampaignSupport, GetCampaignSupportGQL, GetOrganizationDetailsGQL, OrganizationDetailsFragment, UserRole
} from 'generated/graphql';
import { CreateEntityDialog } from 'entity/dialogs/create-entity/create-entity.dialog';
import { SupportDialogComponent } from 'app/main/components/support-dialog/support-dialog.component';
import { frontendVersion } from 'generated/version';
import { AppPersistantStateService, StoreNames } from 'core/state/app-state.service';
import { AppStateStorage } from 'core/state/app-state-storage';
import { WindowRef } from 'core/window-ref.service';

type SupportUserType = GetCampaignSupport.SupportUser | OrganizationDetailsFragment['administratorUser'];

@Component({
  selector: 'app-support-card',
  templateUrl: './support-card.component.html',
})
export class SupportCardComponent extends WithDisposableSubject() implements OnInit, OnDestroy {
  version = frontendVersion;

  isDev = !environment.production;

  /** User displayed in support */
  supportUser: SupportUserType;

  /** Used by view to determine if a campaign is selected */
  isInCampaign = false;

  /** Used by view to determine if the card is expanded / opened */
  isOpen = false;

  currentCampaignId: string;

  campaignId$ = this.route.queryParams.pipe(
    takeUntil(this.disposer),
    map(params => params.campaignId),
    tap((campaignId) => {
      this.currentCampaignId = campaignId;
      // Not currently in a campaign.
      this.isInCampaign = !isNilOrEmpty(campaignId);
    }),
    distinctUntilChanged()
  );

  orgSupport$ = this.getOrganizationDetails.watch().valueChanges
    .pipe(
      takeUntil(this.disposer),
      map(({ data }) => data.getOrganization.administratorUser)
    );

  /**
   *
   */
  constructor(
    private cd: ChangeDetectorRef,
    private route: ActivatedRoute,
    private appPersistantStateService: AppPersistantStateService,
    private getCampaignSupportGQL: GetCampaignSupportGQL,
    private getOrganizationDetails: GetOrganizationDetailsGQL,
    private windowRef: WindowRef,
    private _accountRoleService: AccountRoleService,
    private _dialog: MatDialog,
    private _renderer: Renderer2,
    private _router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
  }

  ngOnInit() {
    this.campaignId$.pipe(
      switchMap<string, any>((campaignId) => {
        if (isNilOrEmpty(campaignId)) {
          return this.orgSupport$;
        }

        return this.getCampaignSupportGQL.fetch({ campaignId })
          .pipe(
            map(({ data }) => data.getCampaign.supportUser)
          );
      })
    )
      .subscribe(supportUser => {
        this.supportUser = supportUser as SupportUserType;
        this.cd.markForCheck();
      });
  }

  enableProdFeatureFlags() {
    this.appPersistantStateService.update(StoreNames.FeatureEnvironment, 'prod');
    this.cd.markForCheck();
  }

  disableProdFeatureFlags() {
    this.appPersistantStateService.update(StoreNames.FeatureEnvironment, '');
    this.cd.markForCheck();
  }

  isProdFeatureFlagEnabled() {
    return this.appPersistantStateService.getValue(StoreNames.FeatureEnvironment) === 'prod';
  }

  openCreateEntityDialog() {
    const dialogRef = CreateEntityDialog.open(this._dialog, {
      data: {
        campaignId: this.currentCampaignId
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.id) {
        this._router.navigate(['/entity/view', result.id], {
          queryParams: { campaignId: this.currentCampaignId }
        });
      }
    });
  }

  addNewButtonVisible() {
    return (this.isInCampaign && this._accountRoleService.userHasMinRole(UserRole.Organizer)) ? true : false;
  }

  openSupportDialog() {
    const panelClass = 'support-dialog';

    this._dialog.open(SupportDialogComponent, {
      panelClass
    });
  }

}
